import React, { useState, useEffect } from 'react'
import {
  formatStatus,
  LEAGUE_STATUS,
  LEAGUE_STARTED_STATUSES,
} from '../../../constants/leagueStatuses'

const getStatusBadgeClass = (status) => {
  const statusClasses = {
    [LEAGUE_STATUS.DRAFT]: 'bg-secondary',
    [LEAGUE_STATUS.REGISTRATION_OPEN]: 'bg-info',
    [LEAGUE_STATUS.REGISTRATION_CLOSED]: 'bg-warning',
    [LEAGUE_STATUS.PENDING_START]: 'bg-primary',
    [LEAGUE_STATUS.ACTIVE]: 'bg-success',
    [LEAGUE_STATUS.IN_PROGRESS]: 'bg-success',
    [LEAGUE_STATUS.PLAYOFFS_SETUP]: 'bg-warning',
    [LEAGUE_STATUS.PLAYOFFS]: 'bg-purple',
    [LEAGUE_STATUS.COMPLETED]: 'bg-dark',
    [LEAGUE_STATUS.CANCELED]: 'bg-danger',
  }
  return statusClasses[status] || 'bg-secondary'
}

/**
 * LeagueDeleteModal - Scary confirmation modal for permanent league deletion
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Object} props.league - League object to be deleted
 * @param {Object} props.user - Current user object (for permission checks)
 * @param {Function} props.onClose - Handler to close modal
 * @param {Function} props.onConfirm - Handler when deletion is confirmed
 * @returns {JSX.Element}
 */
function LeagueDeleteModal({ show, league, user, onClose, onConfirm }) {
  const [confirmUnderstand, setConfirmUnderstand] = useState(false)
  const [confirmResponsible, setConfirmResponsible] = useState(false)

  useEffect(() => {
    if (!show) {
      setConfirmUnderstand(false)
      setConfirmResponsible(false)
    }
  }, [show])

  if (!show || !league) {
    return null
  }

  const handleConfirm = () => {
    if (!confirmUnderstand || !confirmResponsible) {
      return
    }
    onConfirm()
  }

  const isDeleteDisabled = !confirmUnderstand || !confirmResponsible
  const isActive = league.league_status === LEAGUE_STATUS.ACTIVE
  const isCompleted = league.league_status === LEAGUE_STATUS.COMPLETED
  const isStarted = LEAGUE_STARTED_STATUSES.includes(league.league_status)
  const needsOwnerPermission = user?.user_type === 'admin' && isStarted

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-labelledby="delete-modal-title"
      aria-modal="true"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content border-danger">
          <div className="modal-header bg-danger text-white">
            <h4 className="modal-title" id="delete-modal-title">
              <i className="fas fa-exclamation-triangle me-2"></i>
              ⚠️ DANGER: Delete League Permanently
            </h4>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Close modal"
            ></button>
          </div>

          <div className="modal-body">
            <div className="alert alert-danger mb-4" role="alert">
              <h5 className="alert-heading">
                <i className="fas fa-skull-crossbones me-2"></i>
                This action CANNOT be undone!
              </h5>
              <p className="mb-0">
                You are about to permanently delete{' '}
                <strong>"{league.name}"</strong> and ALL associated data.
              </p>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <div className="card border-warning mb-3">
                  <div className="card-header bg-warning">
                    <h6 className="mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      League Information
                    </h6>
                  </div>
                  <div className="card-body">
                    <p>
                      <strong>Name:</strong> {league.name}
                    </p>
                    <p>
                      <strong>Type:</strong> {league.league_type}
                    </p>
                    <p>
                      <strong>Status:</strong>
                      <span
                        className={`badge ms-2 ${getStatusBadgeClass(
                          league.league_status,
                        )}`}
                      >
                        {formatStatus(league.league_status)}
                      </span>
                    </p>
                    <p className="mb-0">
                      <strong>Region:</strong>{' '}
                      {league.region?.city_area || 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div className="card border-danger">
                  <div className="card-header bg-danger text-white">
                    <h6 className="mb-0">
                      <i className="fas fa-trash me-2"></i>
                      Data That Will Be Lost Forever
                    </h6>
                  </div>
                  <div className="card-body">
                    <ul className="mb-0">
                      <li>All league settings and configuration</li>
                      <li>All player registrations and assignments</li>
                      <li>All divisions and rankings</li>
                      <li>All match history and results</li>
                      <li>All availability data</li>
                      <li>All match proposals and scheduling</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Status-specific warnings */}
            {isActive && (
              <div className="alert alert-danger mt-3" role="alert">
                <h6>
                  <i className="fas fa-play me-2"></i>Active League Warning
                </h6>
                <p className="mb-0">
                  This league is currently active with matches in progress.
                  Consider canceling the league instead of deleting it.
                </p>
              </div>
            )}

            {isCompleted && (
              <div className="alert alert-info mt-3" role="alert">
                <h6>
                  <i className="fas fa-check me-2"></i>Completed League Warning
                </h6>
                <p className="mb-0">
                  This league has been completed. Consider archiving the results
                  instead of deleting historical data.
                </p>
              </div>
            )}

            {/* Permission warning */}
            {needsOwnerPermission && (
              <div className="alert alert-warning mt-3" role="alert">
                <h6>
                  <i className="fas fa-user-shield me-2"></i>Permission Required
                </h6>
                <p className="mb-0">
                  Only the league owner can delete leagues that have started.
                  You need owner privileges to perform this action.
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-light border-start border-danger border-4">
              <h6 className="text-danger">
                <i className="fas fa-keyboard me-2"></i>
                Confirmation Required
              </h6>
              <p>To confirm deletion, you understand that:</p>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="confirmUnderstand"
                  checked={confirmUnderstand}
                  onChange={(e) => setConfirmUnderstand(e.target.checked)}
                  aria-describedby="confirm-understand-label"
                />
                <label
                  className="form-check-label"
                  htmlFor="confirmUnderstand"
                  id="confirm-understand-label"
                >
                  I understand this action cannot be undone and all data will be
                  permanently lost
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="confirmResponsible"
                  checked={confirmResponsible}
                  onChange={(e) => setConfirmResponsible(e.target.checked)}
                  aria-describedby="confirm-responsible-label"
                />
                <label
                  className="form-check-label"
                  htmlFor="confirmResponsible"
                  id="confirm-responsible-label"
                >
                  I am authorized and responsible for this deletion
                </label>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              aria-label="Cancel deletion"
            >
              <i className="fas fa-times me-2"></i>
              Cancel (Keep League)
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={isDeleteDisabled}
              title={
                isDeleteDisabled
                  ? 'Check both confirmation boxes to enable deletion'
                  : 'Delete league permanently'
              }
              onClick={handleConfirm}
              aria-label="Confirm permanent deletion"
            >
              <i className="fas fa-trash me-2"></i>
              YES, DELETE PERMANENTLY
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeagueDeleteModal
